import { AnyAction } from "redux";
import { catchError, from, mergeMap, Observable, of } from "rxjs";
import { RootStateOrAny } from 'react-redux';
import { addNotificationFailure, addNotificationSuccess, fetchNotificationsCountFailure, fetchNotificationsCountSuccess, fetchNotificationsFailure, fetchNotificationsSuccess, updateNotificationFailure, updateNotificationSuccess } from "./actions";
import { succeededRequest } from "../request/action";
import { failedRequest } from './../request/action';
import { combineEpics, ofType } from "redux-observable";
import { ADD_NOTIFICATION_REQUEST, FETCH_NOTIFICATION_COUNT_REQUEST, FETCH_NOTIFICATION_REQUEST, UPDATE_NOTIFICATION_REQUEST } from "./actionType";
import { errorString } from "../../utils/strings";
import { addNotificationUseCase, fetchNotificationsCountUseCase, fetchNotificationsUseCase, updateNotificationUseCase } from "../../core/usecases/interactors/notification";
import { data as constants } from "../../constants";

export const addNotificationEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    notificationInteractor: { addNotification },
  }: { notificationInteractor: { addNotification: addNotificationUseCase } }
): Observable<
  | ReturnType<typeof addNotificationSuccess>
  | ReturnType<typeof addNotificationFailure>
  | ReturnType<typeof succeededRequest>
  | ReturnType<typeof failedRequest>
> => action$.pipe(
  ofType(ADD_NOTIFICATION_REQUEST),
  mergeMap(({ payload: { notification } }) => {
    return from(addNotification(notification)).pipe(
      mergeMap(() => {
        return of(addNotificationSuccess(), succeededRequest(ADD_NOTIFICATION_REQUEST, notification));
      }),
      catchError((error) => {
        let errorMessage = errorString(error);

        return of(
          addNotificationFailure(errorMessage),
          failedRequest(ADD_NOTIFICATION_REQUEST, errorMessage)
        )
      })
    )
  })
);

export const fetchNotificationsEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    notificationInteractor: { fetchNotifications },
  }: { notificationInteractor: { fetchNotifications: fetchNotificationsUseCase } }
): Observable<
  ReturnType<typeof succeededRequest> | ReturnType<typeof failedRequest>
> =>
  action$.pipe(
    ofType(FETCH_NOTIFICATION_REQUEST),
    mergeMap(({ payload: { where, startAt, limit } }) => {
      return from(fetchNotifications(where, startAt, limit)).pipe(
        mergeMap((response) => {
          return of(
            succeededRequest(FETCH_NOTIFICATION_REQUEST, {
              ...response,
              where,
              startAt,
              limit,
            })
          );
        }),
        catchError((error) => {
          const errorMessage = errorString(error);
          errorMessage === constants.commonNetworkError &&
            window.location.reload();
          return of(failedRequest(FETCH_NOTIFICATION_REQUEST, errorMessage));
        })
      );
    })
  );

export const updateNotificationEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    notificationInteractor: { updateNotification },
  }: { notificationInteractor: { updateNotification: updateNotificationUseCase } }
): Observable<
  | ReturnType<typeof updateNotificationSuccess>
  | ReturnType<typeof updateNotificationFailure>
  | ReturnType<typeof succeededRequest>
  | ReturnType<typeof failedRequest>
> => action$.pipe(
  ofType(UPDATE_NOTIFICATION_REQUEST),
  mergeMap(({ payload: { id, notification } }) => {
    return from(updateNotification(id, notification)).pipe(
      mergeMap(() => {
        return of(
          succeededRequest(UPDATE_NOTIFICATION_REQUEST, notification),
          updateNotificationSuccess()
        );
      }),
      catchError((error) => {
        let errorMessage = errorString(error);
        return of(
          updateNotificationFailure(errorMessage),
          failedRequest(UPDATE_NOTIFICATION_REQUEST, errorMessage)
        )
      })
    )
  })
)

export const fetchNotificationsCountEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    notificationInteractor: { fetchNotificationsCount },
  }: { notificationInteractor: { fetchNotificationsCount: fetchNotificationsCountUseCase } }
) => action$.pipe(
  ofType(FETCH_NOTIFICATION_COUNT_REQUEST),
  mergeMap(({ payload: { isAdmin, where, startAfter, limit } }) => {
    return from(fetchNotificationsCount(isAdmin, where, startAfter, limit)).pipe(
      mergeMap((response) => of(fetchNotificationsCountSuccess(response))),
      catchError((error) => {
        const errorMessage = errorString(error);
        return of(fetchNotificationsCountFailure(errorMessage));
      })
    )
  })
);

export default combineEpics(
  addNotificationEpic,
  // @ts-ignore
  fetchNotificationsEpic,
  updateNotificationEpic,
  fetchNotificationsCountEpic,
);